<template>
  <div
    class="videoplayer"
    :class="{ mobile }"
    ref="container"
    tabindex="0"
    @keydown.space.prevent="togglePlayPause"
    @keydown.left.prevent="seekBy(-5)"
    @keydown.right.prevent="seekBy(5)"
      @mouseenter="isOver = true"
      @mouseleave="isOver = false"
  >
    <video
      ref="video"
      :poster="poster_url"
      playsinline
      webkit-playsinline
      :crossorigin="crossorigin || null"
      @ended="onEnded"
      @playing="onPlaying"
      @pause="onPaused"
      @timeupdate="onTimeUpdate"
      @progress="calculateBufferRanges"
      @loadedmetadata="onLoadedMetadata"
      @error="onError"
      @click="togglePlayPause"
    >
      <source v-if="mp4_url && !hls_url" :src="mp4_url" type="video/mp4" />

      <track
        v-if="track_url"
        :label="track_label || language"
        kind="subtitles"
        :srclang="track_lang || language"
        :src="track_url"
        default
      />
    </video>

    <div class="controls__bar" :class="{ fixed: !isPlaying || isOver }">
      <div class="controls">
        <button
          class="controls__button"
          @click="togglePlayPause"
          :class="{ play: !isPlaying, pause: isPlaying }"
          :aria-label="isPlaying ? 'Pause' : 'Play'"
        />

        <div
          class="progress-bar"
          ref="progressBar"
          :class="{ active: !disable_seek }"
          role="slider"
          :aria-valuemin="0"
          :aria-valuemax="100"
          :aria-valuenow="progress"
          :aria-disabled="disable_seek ? 'true' : 'false'"
          @click="onProgressClick"
          @mousemove="onProgressHover"
          @mouseleave="timeHover = null"
        >
          <div class="progress-time" v-if="timeHover">{{ timeHover }}</div>

          <span
            class="progress-back"
            :class="{ started: progress !== 0 }"
            :style="{ width: progress + '%' }"
          />

          <div class="progress-ranges">
            <span
              class="progress-range"
              v-for="r in bufferRanges"
              :key="r.index"
              :style="{ left: r.left, width: r.width }"
            />
          </div>
        </div>

        <div class="time">{{ timeRemainingText }}</div>

        <div
          class="full-screen"
          @click="toggleFullScreen"
          :title="isFullscreen ? 'Quitter le plein écran' : 'Plein écran'"
        >
          <span></span>
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </div>

    <div v-if="hadVideoError">Erreur vidéo : lecture impossible.</div>
  </div>
</template>

<script>
import Hls from 'hls.js'

export default {
  name: 'VideoPlayer',
  props: {
    hls_url: { type: String, default: '' },
    mp4_url: { type: String, default: '' },

    poster_url: { type: String, default: '' },
    track_url: { type: String, default: '' },
    track_label: { type: String, default: '' },
    track_lang: { type: String, default: 'fr' },

    disable_seek: { type: Boolean, default: false },
    start_callback: { type: Function },
    end_callback: { type: Function },
    crossorigin: { type: String, default: '' },
    language: { type: String, default: 'fr' },
    mobile: { type: Boolean, default: false },
    autoplay: { type: Boolean, default: false },
    muted: { type: Boolean, default: false }
  },

  emits: [
    'ready',
    'play',
    'pause',
    'ended',
    'timeupdate',
    'error',
    'seeking',
    'seeked',
    'levels',
    'levelchange'
  ],

  data() {
    return {
      video: null,
      progressBar: null,
      container: null,
      hls: null,
      isOver: false,
      isPlaying: false,
      duration: 0,
      progress: 0,
      timeRemaining: 0,
      timeHover: null,
      hadVideoError: false,
      bufferRanges: [],
      isFullscreen: false,

      levels: [],
      currentLevel: -1
    }
  },

  computed: {
    timeRemainingText() {
      return this.secondsToHumanReadable(this.timeRemaining)
    }
  },

  mounted() {
    this.video = this.$refs.video
    this.progressBar = this.$refs.progressBar
    this.container = this.$refs.container

    document.addEventListener('fullscreenchange', this.onFsChange)

    this.loadSource()
  },

  beforeUnmount() {
    document.removeEventListener('fullscreenchange', this.onFsChange)
    this.destroyHls()
  },

  watch: {
    hls_url() {
      this.resetAndReload()
    },
    mp4_url() {
      this.resetAndReload()
    }
  },

  methods: {
    secondsToHumanReadable(total) {
      if (!Number.isFinite(total)) return '00:00'
      total = Math.max(0, Math.round(total))
      const h = Math.floor(total / 3600)
      const m = Math.floor((total % 3600) / 60)
      const s = total % 60
      const HH = h > 0 ? `${String(h).padStart(2, '0')}:` : ''
      return `${HH}${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    },

    shouldUseNativeHls() {
      const ua = navigator.userAgent || ''
      const isSafari = /Safari/i.test(ua) && !/Chrome|Chromium|Android/i.test(ua)
      const isAppleDevice = /iPhone|iPad|iPod|Macintosh/i.test(ua)

      const v = document.createElement('video')
      const canPlayNativeHls =
        !!v.canPlayType('application/vnd.apple.mpegurl') ||
        !!v.canPlayType('application/x-mpegURL')

      return isSafari && isAppleDevice && canPlayNativeHls
    },

    destroyHls() {
      if (this.hls) {
        try {
          this.hls.destroy()
        } catch (e) {
          console.error('Erreur destroy HLS', e)
        }
        this.hls = null
      }
    },

    clearVideoSource() {
      const v = this.video
      if (!v) return

      try {
        v.pause()
      } catch (e) {
        console.error('Error pausing video during source clear', e)
      }

      v.removeAttribute('src')

      while (v.firstChild) {
        const node = v.firstChild
        if (node.tagName === 'SOURCE') {
          v.removeChild(node)
        } else {
          break
        }
      }

      v.load()
    },

    async togglePlayPause() {
      if (this.isPlaying) {
        this.pause()
      } else {
        await this.play()
      }
    },

    async play() {
      if (!this.video) return

      try {
        await this.video.play()
        this.isPlaying = true
      } catch (e) {
        this.isPlaying = false
        console.error('play() failed', e)
      }
    },

    pause() {
      if (!this.video) return
      this.video.pause()
      this.isPlaying = false
    },

    onLoadedMetadata() {
      this.duration = Math.floor(this.video?.duration || 0)
      this.calculateBufferRanges()
      this.$emit('ready')
    },

    onTimeUpdate() {
      if (!this.video || !Number.isFinite(this.video.duration)) return

      const pct = (this.video.currentTime / this.video.duration) * 100
      this.progress = Math.max(0, Math.min(100, pct))
      this.timeRemaining = Math.max(0, this.video.duration - this.video.currentTime)

      this.$emit('timeupdate', {
        currentTime: this.video.currentTime,
        duration: this.video.duration
      })
    },

    onPlaying() {
      this.isPlaying = true
      this.start_callback?.()
      this.$emit('play')
    },

    onPaused() {
      this.isPlaying = false
      this.$emit('pause')
    },

    onEnded() {
      this.isPlaying = false
      this.end_callback?.()
      this.$emit('ended')
    },

    onError(e) {
      console.error('Video error event', e, this.video?.error)
      this.hadVideoError = true
      this.isPlaying = false
      this.$emit('error', e)
    },

    getOffsetPercentage(event) {
      const bar = this.progressBar
      if (!bar || !this.video || !Number.isFinite(this.video.duration)) return 0

      const rect = bar.getBoundingClientRect()
      const x = event.clientX - rect.left
      return Math.max(0, Math.min(1, x / rect.width))
    },

    onProgressClick(e) {
      if (!this.disable_seek) {
        this.seekTo(this.getOffsetPercentage(e))
      }
    },

    onProgressHover(e) {
      const pct = this.getOffsetPercentage(e)
      const seconds = Math.floor((this.video?.duration || 0) * pct)
      this.timeHover = Number.isFinite(seconds)
        ? this.secondsToHumanReadable(seconds)
        : null
    },

    seekTo(pct01) {
      if (!this.video || !Number.isFinite(this.video.duration)) return

      this.$emit('seeking')
      this.video.currentTime = pct01 * this.video.duration
      this.$emit('seeked', this.video.currentTime)
    },

    seekBy(deltaSec) {
      if (!this.video) return

      const d = this.video.duration || 0
      if (!Number.isFinite(d) || d <= 0) return

      const next = Math.max(0, Math.min(d, (this.video.currentTime || 0) + deltaSec))
      this.video.currentTime = next
    },

    calculateBufferRanges() {
      const v = this.video
      if (!v || !Number.isFinite(v.duration) || !v.buffered) return

      const ranges = []
      for (let i = 0; i < v.buffered.length; i++) {
        const startPct = (v.buffered.start(i) / v.duration) * 100
        const endPct = (v.buffered.end(i) / v.duration) * 100
        ranges.push({
          index: i,
          left: `${startPct}%`,
          width: `${endPct - startPct}%`
        })
      }
      this.bufferRanges = ranges
    },

    onFsChange() {
      this.isFullscreen = !!document.fullscreenElement
    },

    async toggleFullScreen() {
      const video = this.$refs.video
      const container = this.$refs.player
      const target = container || video

      if (!target) return

      const isFullscreen =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        video?.webkitDisplayingFullscreen

      try {
        if (!isFullscreen) {
          if (target.requestFullscreen) {
            await target.requestFullscreen({ navigationUI: "hide" })
          } else if (target.webkitRequestFullscreen) {
            target.webkitRequestFullscreen()
          } else if (video?.webkitEnterFullscreen) {
            video.webkitEnterFullscreen()
          }
        } else {
          if (document.exitFullscreen) {
            await document.exitFullscreen()
          } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen()
          } else if (video?.webkitExitFullscreen) {
            video.webkitExitFullscreen()
          }
        }
      } catch (err) {
        console.warn("Fullscreen failed:", err)
      }
    },

    async loadSource() {
      const v = this.video
      if (!v) return

      v.autoplay = !!this.autoplay
      v.muted = !!this.muted

      this.destroyHls()
      this.hadVideoError = false
      this.levels = []
      this.currentLevel = -1
      this.isPlaying = false
      this.progress = 0
      this.timeRemaining = 0
      this.bufferRanges = []

      this.clearVideoSource()

      if (this.hls_url) {
        const useNativeHls = this.shouldUseNativeHls()

        if (!useNativeHls && Hls.isSupported()) {
          this.hls = new Hls({
            enableWorker: false,
            lowLatencyMode: false
          })

          this.hls.attachMedia(v)

          this.hls.on(Hls.Events.MEDIA_ATTACHED, () => {
            this.hls.loadSource(this.hls_url)
          })

          this.hls.on(Hls.Events.MANIFEST_PARSED, (_e, data) => {
            this.levels = (data.levels || []).map((l, i) => ({
              index: i,
              height: l.height,
              bitrate: l.bitrate,
              name: l.name || (l.height ? `${l.height}p` : `${Math.round(l.bitrate / 1000)} kbps`)
            }))

            this.$emit('levels', this.levels)

            if (this.autoplay) {
              v.play().catch(err => {
                console.error('Autoplay HLS failed', err)
              })
            }
          })

          this.hls.on(Hls.Events.LEVEL_SWITCHED, (_e, data) => {
            this.currentLevel = data.level
            this.$emit('levelchange', this.currentLevel)
          })

          this.hls.on(Hls.Events.ERROR, (_e, data) => {
            console.error('HLS error', data)

            if (!data?.fatal) return

            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                try {
                  this.hls.startLoad()
                } catch (err) {
                  console.error('HLS startLoad failed', err)
                  this.hadVideoError = true
                }
                break

              case Hls.ErrorTypes.MEDIA_ERROR:
                try {
                  this.hls.recoverMediaError()
                } catch (err) {
                  console.error('HLS recoverMediaError failed', err)
                  this.hadVideoError = true
                }
                break

              default:
                this.hadVideoError = true
                this.destroyHls()
                break
            }
          })
        } else {
          v.src = this.hls_url
          v.load()

          if (this.autoplay) {
            v.play().catch(err => {
              console.error('Native HLS autoplay failed', err)
            })
          }
        }

        return
      }

      if (this.mp4_url) {
        v.src = this.mp4_url
        v.load()

        if (this.autoplay) {
          v.play().catch(err => {
            console.error('MP4 autoplay failed', err)
          })
        }

        return
      }

      this.hadVideoError = true
    },

    setLevel(index) {
      if (this.hls && Hls.isSupported()) {
        this.hls.currentLevel = index
      }
    },

    resetAndReload() {
      this.loadSource()
    }
  }
}
</script>

<style scoped>
video {
  display: block;
  margin-inline: auto;
  width: min(100%, 40em);
  aspect-ratio: 16/9;
  border-radius: 1em;
}

.mobile video {
}

video[poster] {
  object-fit: cover;
}

.videoplayer {
  position: relative;
  display: block;
  width: min(40rem, 100%);
  margin-inline: auto;
  min-height: 10em;
}

.videoplayer:hover .controls {
  opacity: 1;
  visibility: visible;
}

.videoplayer:focus {
  outline: none;
}

.videoplayer video {
  display: block;
  width: 100%;
}

.videoplayer .controls {
  position: absolute;
  left: 1em;
  bottom: 1em;
  right: 1em;
  padding: 0.25em 1em;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.videoplayer .controls .controls__button {
  flex: 0 0 4em;
  display: block;
  position: relative;
  height: 4em;
  background-image: linear-gradient(180deg, #023570 0%, #017DAC 100%);
  border-radius: 50%;
  outline: solid 4px #ffffff50;
  border: none;
}

.videoplayer .controls .controls__button:hover {
  cursor: pointer;
}

.videoplayer .controls .controls__button:before {
  content: '';
  position: absolute;
}

.videoplayer .controls .controls__button.play:before {
  left: 50%;
  top: 50%;
  margin: -1em 0 0 -0.5em;
  width: 0;
  height: 0;
  border-top: 1em solid transparent;
  border-bottom: 1em solid transparent;
  border-left: 1.5em solid white;
}

.videoplayer .controls .controls__button.pause:before {
  height: 2em;
  width: 1.5em;
  background-image: linear-gradient(
    to left,
    white,
    white 30%,
    transparent 31%,
    transparent 69%,
    white 70%,
    white 100%
  );
  top: 1em;
}

.videoplayer .controls .controls__button.pause:before {
  left: calc(50% - 0.7em);
}

.controls__bar {
  display: flex;
  position: absolute;
  width: 100%;
  flex-grow: 1;
  align-items: center;
  background-image: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 100%);
  border-bottom-left-radius: 1em;
  border-bottom-right-radius: 1em;
  height: 6em;
  bottom: 0;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
}

.videoplayer .controls__bar.fixed {
  opacity: 1;
  visibility: visible;
}

.progress-bar {
  flex: 1;
  margin: 0 1.5em;
  width: 100%;
  height: 0.5em;
  border-radius: 0.25em;
  background-color: rgba(255, 255, 255, 0.4);
  position: relative;
}

.progress-bar.active:hover {
  cursor: pointer;
}

.progress-bar:hover .progress-time {
  display: block;
}

.progress-time {
  display: none;
  position: absolute;
  top: -3.5em;
  left: 50%;
  background-color: rgba(255, 255, 255, 0.7);
  color: #fff;
  padding: 0.25em 1em;
  border-top-left-radius: 0.5em;
  border-top-right-radius: 0.5em;
}

.progress-back {
  position: absolute;
  height: 0.5em;
  top: 0;
  left: 0;
  width: 0;
  background-color: var(--clr-white);
  border-radius: 0.25em;
  z-index: 2;
}

.progress-ranges {
  position: absolute;
  height: 0.5em;
  top: 0;
  left: 0;
  right: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
}

.progress-range {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 0;
  border-radius: 0.25em;
  background-color: grey;
  pointer-events: none;
}

.time {
  color: white;
  font-size: 1em;
  margin-right: 1em;
}

.VJS_controls__volume {
  width: 2.5em;
  height: 1.5em;
  position: relative;
}

.VJS_controls__volume-bar {
  width: 0.25em;
  position: absolute;
  bottom: 0;
  background-color: white;
  transition: height 0.25s ease;
}

.VJS_controls__volume-bar--active {
  background-color: #33ccff;
}

.VJS_controls__volume-bar:hover {
  cursor: pointer;
}

.VJS_controls__volume-bar:first-child {
  left: 0;
  height: 5px;
}
.VJS_controls__volume-bar:nth-child(2) {
  left: 5px;
  height: 8px;
}
.VJS_controls__volume-bar:nth-child(3) {
  left: 10px;
  height: 12px;
}
.VJS_controls__volume-bar:nth-child(4) {
  left: 15px;
  height: 15px;
}
.VJS_controls__volume-bar:last-child {
  left: 20px;
  height: 18px;
}
.VJS_controls__volume-bar:first-child:hover {
  height: 8px;
}
.VJS_controls__volume-bar:nth-child(2):hover {
  height: 11px;
}
.VJS_controls__volume-bar:nth-child(3):hover {
  height: 15px;
}
.VJS_controls__volume-bar:nth-child(4):hover {
  height: 18px;
}
.VJS_controls__volume-bar:last-child:hover {
  height: 21px;
}

.full-screen {
  width: 3em;
  height: 2em;
  position: relative;
  transition: transform 0.2s ease;
  margin-right: 1em;
  --border-color: var(--clr-white);
  --border-width: 4px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.3em;
}

.full-screen:hover {
  transform: scale(1.05);
  cursor: pointer;
}

.full-screen span {
  width: 1em;
  height: 0.8em;
  display: block;
  justify-self: center;
}

@media screen and (orientation: portrait) {
  .mobile .controls {
    padding-inline: 0;
    bottom: 0;
  }

  .mobile .videoplayer .controls .controls__button {
    flex: 0 0 2em;
    background-image: none;
    background-color: transparent;
    outline: none;
  }

  .mobile .full-screen {
    --border-width: 2px;
    margin-right: 0;
  }
}

.full-screen span:first-child {
  top: 0;
  left: 0;
  border-left: var(--border-width) solid var(--border-color);
  border-top: var(--border-width) solid var(--border-color);
}

.full-screen span:nth-child(2) {
  top: 0;
  right: 0;
  border-top: var(--border-width) solid var(--border-color);
  border-right: var(--border-width) solid var(--border-color);
}

.full-screen span:nth-child(3) {
  bottom: 0;
  left: 0;
  border-left: var(--border-width) solid var(--border-color);
  border-bottom: var(--border-width) solid var(--border-color);
}

.full-screen span:last-child {
  bottom: 0;
  right: 0;
  border-bottom: var(--border-width) solid var(--border-color);
  border-right: var(--border-width) solid var(--border-color);
}
</style>